import React, { type ReactElement, type ReactNode, type SyntheticEvent, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grow from '@mui/material/Grow';
import LinearProgress from '@mui/material/LinearProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import clsx from 'clsx';
import PowerIconButton from './PowerIconButton';
import mergeSxProps from '../shared/mergeSxProps';
import { shakeAnimation } from '../shared/shakeAnimation';
import { useDialogFullScreen } from '../hooks/useDialogFullScreen';

type DialogBoxProps = {
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  title?: ReactElement<unknown> | string;
  content?: ReactElement<unknown> | string;
  confirmLabel?: ReactElement<unknown> | string;
  cancelLabel?: ReactElement<unknown> | string;
  hideConfirm?: boolean;
  hideCancel?: boolean;
  /** Shows a header close (`xmark`) PowerIconButton that invokes `onCancel`. */
  showClose?: boolean;
  /** Tooltip / accessible name for the close button. */
  closeTitle?: ReactNode;
  /** Shows a header back (`arrow-left`) PowerIconButton. */
  showBack?: boolean;
  /** Tooltip / accessible name for the back button. */
  backTitle?: ReactNode;
  /** Called when the optional back button is clicked. */
  onBack?: (event: SyntheticEvent) => void;
  onConfirm: (event: SyntheticEvent) => void;
  onCancel: (event: SyntheticEvent) => void;
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;

  /** When false, omits `DialogContent` dividers. Defaults to true. */
  contentDividers?: boolean;

  /**
   * When false, renders `children` without a wrapping `DialogContent`
   * (for panels that supply their own `DialogContent` / `DialogActions`).
   */
  wrapContent?: boolean;

  /** Left-aligned content in `DialogActions` (e.g. IconFabs). */
  extraActions?: ReactNode;

  /**
   * When set, replaces the default cancel/confirm buttons on the right side of
   * `DialogActions`. Use with `extraActions` for fully custom footers.
   */
  actions?: ReactNode;
  slotProps?: DialogProps['slotProps'];
  sx?: SxProps<Theme>;
  shake?: boolean;
  setShake?: (shake: boolean) => void;
  loading?: boolean;
};

/**
 * Shared modal shell with optional confirm/cancel actions and optional header back/close icon buttons.
 * @param props Dialog open state, labels, actions, and optional header navigation.
 * @returns The dialog element.
 */
export default function DialogBox(props: DialogBoxProps) {
  'use memo';

  const {
    isOpen,
    children,
    className,
    title,
    content,
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    hideConfirm,
    hideCancel,
    showClose = false,
    closeTitle = 'Close',
    showBack = false,
    backTitle = 'Back',
    onBack,
    onConfirm,
    onCancel,
    maxWidth,
    fullWidth,
    contentDividers = true,
    wrapContent = true,
    extraActions,
    actions,
    slotProps,
    sx,
    shake = false,
    setShake,
    loading = false,
  } = props;

  const fullScreen = useDialogFullScreen();
  const hasHeaderIconButtons = showClose || showBack;
  const showHeader = Boolean(title) || hasHeaderIconButtons;
  const showActions = Boolean(extraActions) || Boolean(actions) || !hideConfirm || !hideCancel;

  useEffect(() => {
    if (shake && setShake) {
      const timer = setTimeout(() => setShake(false), 600);
      return () => clearTimeout(timer);
    }
  }, [shake, setShake]);

  const confirmDialog = (event: React.SyntheticEvent) => {
    if (event) {
      event.persist();
      event.preventDefault();
      event.stopPropagation();
    }
    onConfirm(event);
  };

  const cancelDialog = (event: React.SyntheticEvent) => {
    if (event) {
      event.persist();
      event.preventDefault();
      event.stopPropagation();
    }
    onCancel(event);
  };

  const backDialog = (event: React.SyntheticEvent) => {
    if (event) {
      event.persist();
      event.preventDefault();
      event.stopPropagation();
    }
    onBack?.(event);
  };

  const body = (
    <>
      {content && (
        <DialogContentText
          component="div"
          className="dialog-text"
          sx={children ? { mb: 4 } : undefined}
        >
          {content}
        </DialogContentText>
      )}
      {children}
    </>
  );

  return (
    <Dialog
      className={clsx('dialog-box', className)}
      onMouseUp={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      open={isOpen}
      onClose={cancelDialog}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      scroll={'paper'}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-text"
      slots={{
        transition: Grow,
      }}
      slotProps={slotProps}
      sx={mergeSxProps(
        sx,
        shake
          ? {
              '& .MuiDialog-paper': {
                ...shakeAnimation,
              },
            }
          : undefined,
      )}
    >
      {showHeader && (
        <DialogTitle
          className="dialog-title"
          id="dialog-title"
          sx={
            hasHeaderIconButtons
              ? { justifyContent: 'center', flexShrink: 0, position: 'relative' }
              : undefined
          }
        >
          {showBack && (
            <Box
              sx={{
                position: 'absolute',
                left: (theme) => theme.spacing(2),
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <PowerIconButton
                icon="arrow-left"
                aria-label={typeof backTitle === 'string' ? backTitle : 'Back'}
                onClick={backDialog}
                title={backTitle}
              />
            </Box>
          )}
          {title}
          {showClose && (
            <Box
              sx={{
                position: 'absolute',
                right: (theme) => theme.spacing(2),
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <PowerIconButton
                icon="xmark"
                aria-label="close"
                onClick={cancelDialog}
                title={closeTitle}
                disabled={loading}
              />
            </Box>
          )}
        </DialogTitle>
      )}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            height: 2,
          }}
        />
      )}
      {wrapContent ? <DialogContent dividers={contentDividers}>{body}</DialogContent> : body}
      {showActions && (
        <DialogActions>
          <Box
            className="dialog-actions-layout"
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              className="dialog-extra-actions"
              sx={{ display: 'flex', alignItems: 'center', gap: 4, justifySelf: 'start' }}
            >
              {extraActions}
            </Box>
            <Box
              className="dialog-primary-actions"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, justifySelf: 'center' }}
            >
              {actions ?? (
                <>
                  {!hideCancel && (
                    <Button className="cancel-button" onClick={cancelDialog} disabled={loading}>
                      {cancelLabel}
                    </Button>
                  )}
                  {!hideConfirm && (
                    <Button
                      className="confirm-button"
                      onClick={confirmDialog}
                      color="primary"
                      disabled={loading}
                    >
                      {confirmLabel}
                    </Button>
                  )}
                </>
              )}
            </Box>
            <Box aria-hidden sx={{ justifySelf: 'end' }} />
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
